import { setHoveredDate } from "@common-reducers/WhatsAppReducer";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { handleNewMessageDialogThunk } from "@common-reducers/CalendarThunks";
import { memo, useCallback } from "react";
import classes from "./message-calendar.module.scss";

type Props = {
    date: Date;
    children?: React.ReactNode;
}

const CustomMonthDayBackground = ({ children, date }: Props) => {
    const dispatch = useAppDispatch();

    // Check if dialog is open - don't trigger slot selection if dialog is already open
    const isDialogOpen = useAppSelector(state => state.AddScheduledMessageButtonReducer.open);

    // Memoize event handlers to prevent unnecessary re-renders
    const handleMouseEnter = useCallback(() => {
        dispatch(setHoveredDate(date));
    }, [dispatch, date]);

    const handleMouseLeave = useCallback(() => {
        dispatch(setHoveredDate(undefined));
    }, [dispatch]);

    const handleClick = useCallback((e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const isEvent = target?.closest('.rbc-event') !== null;
        const isDialogElement = target?.closest('[role="dialog"]') !== null ||
                               target?.closest('[data-radix-dialog-overlay]') !== null;

        // Don't trigger if dialog is open or click is on dialog element
        // This prevents re-triggering when clicking backdrop to dismiss
        if (isDialogOpen || isDialogElement) {
            return;
        }

        // Only trigger dialog if clicking on empty space (not on an event)
        // Events should be handled by onSelectEvent
        if (!isEvent) {
            // Create slotInfo object similar to what react-big-calendar would provide
            const slotInfo = {
                start: date,
                end: date,
                slots: [date],
                action: 'click' as const,
            };
            dispatch(handleNewMessageDialogThunk({ slotInfo }));
        }
    }, [date, isDialogOpen, dispatch]);

    return (
        <div 
            className={classes.monthDayBackground} 
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            {children}
        </div>
    );
};

export default memo(CustomMonthDayBackground)